import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NumberFormat from "react-number-format";

import * as Yup from "yup";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { Autocomplete, Button, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useActionHook } from "hooks";
import { debounce } from "lodash";
import { useDealActions } from "store";
import { IAssetClass, ReportCashflowAllocationTarget, ReportCashflowAum } from "store/types";

import { IAllocationTarget } from "./AllocationTarget";

interface IProps {
  onClose: () => void;
  updateAllocationTarget: (data: { aum_id: number; targets: IAllocationTarget[] }) => void;
  allocationTargetData: ReportCashflowAllocationTarget[];
  aumData: ReportCashflowAum | null;
}

const validationSchema = Yup.object().shape({
  targets: Yup.array().of(
    Yup.object().shape({
      share: Yup.number().min(0, "Share must be between 0 and 100").max(100, "Share must be between 0 and 100"),
      asset_class_id: Yup.number().min(1, "Asset class is required").required("Asset class is required"),
      years: Yup.number()
        .min(1, "Invalid input. The model supports forecasts up to 10 years. Please enter a number between 1 and 10.")
        .max(10, "Invalid input. The model supports forecasts up to 10 years. Please enter a number between 1 and 10.")
    })
  )
});

export const PopoverContent: React.FC<IProps> = ({
  onClose,
  updateAllocationTarget,
  allocationTargetData,
  aumData
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { getAssetClassesAction } = useDealActions();

  const {
    data: assetClasses,
    loading: assetClassesLoading,
    action: getAssetClasses
  } = useActionHook(getAssetClassesAction);

  useEffect(() => {
    getAssetClasses();
  }, []);

  const formik = useFormik({
    initialValues: {
      targets: allocationTargetData.length
        ? allocationTargetData.map((target) => ({
            id: target.id,
            share: Number(target.share),
            asset_class_id: target.asset_class_id,
            years: target.years,
            is_deleted: false
          }))
        : [
            {
              id: String(Date.now()),
              share: 0,
              asset_class_id: 0,
              years: 1,
              is_deleted: false
            }
          ]
    },
    validationSchema,
    onSubmit: (values) => {
      if (!aumData?.id) return;
      updateAllocationTarget({
        aum_id: aumData.id,
        targets: values.targets
      });
    }
  });

  const handleAddRow = () => {
    formik.setFieldValue("targets", [
      ...formik.values.targets,
      { id: String(Date.now()), share: 0, asset_class_id: 0, years: 1, is_deleted: false }
    ]);

    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }, 0);
  };

  const getTargetIndexById = (targetId: string | number) => {
    return formik.values.targets.findIndex((t) => t.id === targetId);
  };

  //check if the asset class is already selected
  const getAvailableAssetClasses = (currentTargetId: string | number) => {
    const selectedAssetClassIds = formik.values.targets
      .filter((t) => !t.is_deleted && t.id !== currentTargetId)
      .map((t) => t.asset_class_id);

    return assetClasses?.filter((ac) => !selectedAssetClassIds.includes(ac.id)) || [];
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedSubmit = useCallback(
    debounce(async () => {
      if (isSubmitting) return;
      try {
        await formik.submitForm();
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    }, 2000),
    [formik, onClose, isSubmitting]
  );

  const isDisabled = useMemo(
    () => isSubmitting || formik.isSubmitting,
    [formik.isValid, isSubmitting, formik.isSubmitting]
  );

  return (
    <FormikProvider value={formik}>
      <ProgressOverlay containerSX={{ height: "100%", width: "490px" }} inProgress={assetClassesLoading}>
        <Typography sx={{ padding: "20px" }}>Allocation target</Typography>
        {Boolean(formik.values.targets.length) && (
          <Stack
            ref={scrollContainerRef}
            direction="column"
            gap="20px"
            sx={{ maxHeight: "170px", overflow: "auto", padding: "5px 20px" }}
          >
            <FieldArray
              name="targets"
              render={() => (
                <>
                  {formik.values.targets
                    .filter((target) => !target.is_deleted)
                    .map((target) => {
                      const index = getTargetIndexById(target.id);
                      return (
                        <Stack key={target.id} direction="row" gap="20px" alignItems="center">
                          <NumberFormat
                            disabled={isDisabled}
                            style={{ width: "85px" }}
                            label="Share"
                            placeholder="0"
                            customInput={TextField}
                            slotProps={{
                              input: {
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                              }
                            }}
                            allowNegative={false}
                            isNumericString
                            decimalScale={2}
                            value={target.share}
                            onValueChange={(values) => {
                              const newValue = Number(values.value) > 100 ? 100 : Number(values.value);
                              formik.setFieldValue(`targets.${index}.share`, newValue);
                            }}
                            error={Boolean(formik.getFieldMeta(`targets.${index}.share`).error)}
                          />

                          <Autocomplete<IAssetClass, false>
                            disabled={isDisabled}
                            sx={{ width: "200px" }}
                            fullWidth
                            options={getAvailableAssetClasses(target.id)}
                            value={assetClasses?.find((ac) => ac.id === target.asset_class_id) || null}
                            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IAssetClass) => (
                              <li {...props} key={option.id}>
                                {option.presentation_name}
                              </li>
                            )}
                            getOptionLabel={(option: IAssetClass) => option.presentation_name}
                            onChange={(_, option: IAssetClass | null) => {
                              formik.setFieldValue(`targets.${index}.asset_class_id`, option ? option.id : 0);
                            }}
                            renderInput={(params) => (
                              <TextField label="Asset Classes" placeholder="Select" {...params} />
                            )}
                          />

                          <Tooltip title={formik.getFieldMeta(`targets.${index}.years`).error || ""}>
                            <Stack>
                              <NumberFormat
                                disabled={isDisabled}
                                style={{ width: "85px" }}
                                fullWidth
                                label="Years"
                                placeholder="0"
                                customInput={TextField}
                                allowNegative={false}
                                isNumericString
                                decimalScale={2}
                                value={target.years}
                                onValueChange={(values) => {
                                  formik.setFieldValue(`targets.${index}.years`, Number(values.value));
                                }}
                                error={Boolean(formik.getFieldMeta(`targets.${index}.years`).error)}
                              />
                            </Stack>
                          </Tooltip>

                          <Stack sx={{ width: "24px" }}>
                            <IconButton
                              disabled={isDisabled}
                              sx={{ bgcolor: "#F5F6F7" }}
                              size="sm"
                              onClick={() => {
                                formik.setFieldValue(`targets.${index}.is_deleted`, true);
                              }}
                            >
                              <ClearIcon />
                            </IconButton>
                          </Stack>
                        </Stack>
                      );
                    })}
                </>
              )}
            />
          </Stack>
        )}

        <Stack direction="column" gap="20px" sx={{ padding: "0 20px 20px 20px" }}>
          <IconButton disabled={isDisabled} onClick={handleAddRow} sx={{ bgcolor: "#F5F6F7" }}>
            <AddIcon />
          </IconButton>

          <Stack direction="row" justifyContent="flex-end" gap="10px">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!formik.isValid || isDisabled}
              onClick={() => {
                setIsSubmitting(true);
                debouncedSubmit();
              }}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      </ProgressOverlay>
    </FormikProvider>
  );
};
